import { ICellRendererAngularComp } from '@ag-grid-community/angular';
import { ICellRendererParams, IRowNode } from '@ag-grid-community/core';
import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, Signal } from '@angular/core';

import { IconComponent } from '@shared/components/icon/icon.component';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { injectPermissions } from '@shared/store/auth/auth.service';
import { Maybe } from '@shared/utils/utils';

export type AgInvoiceActionsComponentParamsInput = {
  disableDownloadButton?: boolean;
  downloadClickFN?: (params: { rowNode: IRowNode }) => void;
  downloadLinesClickFN?: (params: { rowNode: IRowNode }) => void;
  hideDownloadButton?: boolean;
  iCloseMonthsProcessing: Signal<boolean>;
  invoiceLockTooltip: Signal<string>;
  isInvoiceFinalized: Signal<boolean>;
  shouldEnableCSVDownload?: boolean;
  uploadClickFN:
    | ((params: { instance: AgInvoiceActionsComponent; rowNode: IRowNode }) => Promise<void> | void)
    | undefined;
};

type AgInvoiceActionsComponentParams = AgInvoiceActionsComponentParamsInput & ICellRendererParams;

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TooltipDirective, IconComponent, NgClass],
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
  templateUrl: './ag-invoice-actions.component.html',
})
export class AgInvoiceActionsComponent implements ICellRendererAngularComp {
  permissions = injectPermissions({
    hasEditInvoice: 'PERMISSION_UPDATE_INVOICES',
  });

  params!: AgInvoiceActionsComponentParams;

  disabled = computed(() => {
    return (
      this.params.iCloseMonthsProcessing() ||
      this.params.isInvoiceFinalized() ||
      !this.permissions().hasEditInvoice
    );
  });
  disabledTooltip = computed(() => {
    if (!this.permissions().hasEditInvoice) {
      return MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION;
    }

    return '';
  });

  enableCSV = false;
  fileExist = false;

  agInit(params: AgInvoiceActionsComponentParams): void {
    this.params = params;

    if (!this.params.value) {
      this.params.disableDownloadButton = true;
    }

    this.fileExist = !!this.params.node.data.file;

    this.params.shouldEnableCSVDownload =
      (params.node.data.line_items && params.node.data.line_items.length > 0) ||
      (params.node.data.ocr_line_items && params.node.data.ocr_line_items.length > 0);

    this.enableCSV = !!this.params.shouldEnableCSVDownload;
  }

  onDownloadClick() {
    if (this.params.downloadClickFN) {
      this.params.downloadClickFN({ rowNode: this.params.node });
    }
  }

  onDownloadLinesClick() {
    if (this.params.downloadLinesClickFN) {
      this.params.downloadLinesClickFN({ rowNode: this.params.node });
    }
  }

  async onUploadClick() {
    if (this.params.uploadClickFN) {
      await this.params.uploadClickFN({
        instance: this,
        rowNode: this.params.node,
      });
    }
  }

  refresh(): boolean {
    return false;
  }

  refreshFileState(file: Maybe<unknown[]>) {
    if (file) {
      if (file.length === 0) {
        this.params.node.data.file = false;
      } else {
        this.params.node.data.file = file[0];
      }
    }
  }
}
